// client/src/components/Insights/tabs/SeasonalTab.jsx
// Which calendar months cost more, from up to two years of tracked history.
import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Alert, Box, Button, CardContent, Chip, Grid, Skeleton, Stack, Tooltip, Typography } from '@mui/material';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RTooltip, ResponsiveContainer, Legend } from 'recharts';
import { Activity, Lightbulb } from 'lucide-react';
import { useCurrency } from '../../../contexts/CurrencyContext';
import { GlassCard, SectionTitle } from '../shared/SharedComponents';
import { alpha, gradientTextSx } from '../shared/constants';
import { getSeasonalForecast } from '../../../services/insightsAPI';

const SeasonalTab = () => {
  const { t, i18n } = useTranslation();
  const { formatAmount } = useCurrency();
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['seasonalForecast'],
    queryFn: () => getSeasonalForecast({}),
    select: (d) => d?.data || d,
  });

  if (isLoading) return <Skeleton variant="rounded" height={400} sx={{ borderRadius: 3 }} />;
  if (error || !data) {
    return (
      <Alert severity="warning" action={<Button onClick={refetch} size="small">{t('aiInsights.common.retry', 'Retry')}</Button>}>
        {t('aiInsights.seasonalV2.loadError', 'Could not load your seasonal pattern.')}
      </Alert>
    );
  }

  const name = (n, style = 'short') => new Intl.DateTimeFormat(i18n.language, { month: style, timeZone: 'UTC' }).format(new Date(Date.UTC(2026, n - 1, 15)));
  const byNum = Object.fromEntries((data.months || []).map((m) => [m.monthNum, m]));
  const all = Array.from({ length: 12 }, (_, i) => byNum[i + 1] || { monthNum: i + 1, missing: true, expenses: 0, income: 0 });
  const maxExpense = Math.max(...all.map((m) => m.expenses || 0), 1);
  const chart = all.filter((m) => !m.missing).map((m) => ({ ...m, label: name(m.monthNum) }));
  const list = (nums) => nums.map((n) => name(n, 'long')).join(', ');

  return (
    <Box>
      <SectionTitle icon={Activity}>{t('aiInsights.seasonal.title', 'Seasonal Intelligence')}</SectionTitle>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {t('aiInsights.seasonalV2.lead', { count: data.window?.months || 0, defaultValue: 'Average spending per calendar month, from your last {{count}} tracked months. Transfers are left out.' })}
      </Typography>

      {!data.enough && (
        <Alert severity="info" sx={{ mb: 3, borderRadius: 2 }}>
          {t('aiInsights.seasonalV2.notEnough', { count: data.monthsCovered || 0, defaultValue: 'You have {{count}} of the 12 calendar months tracked so far. Months are ranked once each one has been seen at least once.' })}
        </Alert>
      )}

      <GlassCard sx={{ mb: 3 }}>
        <CardContent>
          <Typography fontWeight={800} mb={1.5} sx={gradientTextSx}>{t('aiInsights.seasonal.heatmap', 'Financial Year Heatmap')}</Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(6, 1fr)', sm: 'repeat(12, 1fr)' }, gap: 0.5 }}>
            {all.map((m) => {
              const intensity = (m.expenses || 0) / maxExpense;
              return (
                <Tooltip
                  key={m.monthNum}
                  title={m.missing
                    ? t('aiInsights.seasonalV2.noData', 'No data yet')
                    : `${name(m.monthNum, 'long')}: ${formatAmount(m.expenses)}`}
                >
                  <Box sx={{
                    height: 44, borderRadius: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
                    bgcolor: m.missing ? 'action.hover' : alpha('#ef4444', 0.08 + intensity * 0.6),
                    border: m.missing ? '1px dashed' : 'none', borderColor: 'divider',
                  }}
                  >
                    <Typography sx={{ fontSize: 11, fontWeight: 700, color: !m.missing && intensity > 0.6 ? '#fff' : 'text.secondary' }}>{name(m.monthNum)}</Typography>
                  </Box>
                </Tooltip>
              );
            })}
          </Box>
        </CardContent>
      </GlassCard>

      {data.enough && (
        <Grid container spacing={2} mb={3}>
          <Grid item xs={12} md={6}>
            <GlassCard sx={{ borderLeft: '3px solid #ef4444', height: '100%' }}>
              <CardContent>
                <Typography variant="body2" fontWeight={800}>{t('aiInsights.seasonalV2.expensive', 'Most expensive months')}</Typography>
                <Typography variant="body2">{list(data.expensive)}</Typography>
              </CardContent>
            </GlassCard>
          </Grid>
          <Grid item xs={12} md={6}>
            <GlassCard sx={{ borderLeft: '3px solid #10b981', height: '100%' }}>
              <CardContent>
                <Typography variant="body2" fontWeight={800}>{t('aiInsights.seasonalV2.friendly', 'Lightest months')}</Typography>
                <Typography variant="body2">{list(data.friendly)}</Typography>
              </CardContent>
            </GlassCard>
          </Grid>
        </Grid>
      )}

      {data.current && (
        <Alert severity="info" icon={<Lightbulb size={18} />} sx={{ mb: 3, borderRadius: 2 }}>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            <span>
              {t('aiInsights.seasonalV2.current', {
                month: name(data.current.monthNum, 'long'),
                expenses: formatAmount(data.current.expenses),
                defaultValue: 'In past years, {{month}} cost you about {{expenses}}.',
              })}
            </span>
            {data.current.pctVsAverage !== 0 && (
              <Chip
                size="small"
                label={t('aiInsights.seasonalV2.vsAverage', { pct: `${data.current.pctVsAverage > 0 ? '+' : ''}${data.current.pctVsAverage}`, defaultValue: '{{pct}}% vs your average month' })}
                color={data.current.pctVsAverage > 10 ? 'warning' : 'default'}
              />
            )}
          </Stack>
        </Alert>
      )}

      {chart.length > 0 && (
        <GlassCard>
          <CardContent>
            <Typography fontWeight={800} mb={2}>{t('aiInsights.seasonal.chart', 'Income vs Expenses by Month')}</Typography>
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={chart}>
                <CartesianGrid strokeDasharray="3 3" opacity={0.15} />
                <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => formatAmount(v)} width={80} />
                <RTooltip formatter={(v) => formatAmount(v)} />
                <Legend />
                <Bar dataKey="income" fill="#10b981" name={t('aiInsights.seasonalV2.income', 'Income')} radius={[4, 4, 0, 0]} />
                <Bar dataKey="expenses" fill="#ef4444" name={t('aiInsights.seasonalV2.expenses', 'Spending')} radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </GlassCard>
      )}
    </Box>
  );
};

export default SeasonalTab;
